import { useState } from "react";
import { Plus, MessageSquare, GitBranch, Pin, Trash2, RotateCcw } from "lucide-react";
import { Button } from "../ui/button";

function groupByParent(conversations) {
  const byParent = {};
  (conversations || []).forEach((c) => {
    const key = c.parent_conversation_id || "root";
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(c);
  });
  return byParent;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

const ACTION = "shrink-0 p-1 rounded text-muted-foreground opacity-60 hover:opacity-100 hover:bg-muted focus-visible:opacity-100";

// El árbol (chat → sus temas → los temas de un tema) se aplana en filas con su profundidad: así la fila no necesita llamarse a sí misma.
function flattenTree(roots, byParent) {
  const rows = [];
  const stack = [...roots].reverse().map((conversation) => ({ conversation, depth: 0 }));
  while (stack.length) {
    const { conversation, depth } = stack.pop();
    rows.push({ conversation, depth });
    [...(byParent[conversation.id] || [])].reverse().forEach((child) => stack.push({ conversation: child, depth: depth + 1 }));
  }
  return rows;
}

function ConversationRow({ conversation, depth, selectedConversationId, onSelect, onMakeMain, onDelete }) {
  const isTema = conversation.kind === "tema";
  const isMain = !!conversation.is_main;
  return (
    <div>
      <div className="flex items-center gap-0.5 group" style={{ paddingLeft: `${depth * 16}px` }}>
        <button
          key={conversation.id}
          data-testid={`conversation-item-${conversation.id}`}
          onClick={() => onSelect(conversation.id)}
          className={`min-w-0 flex-1 text-left py-2 pl-3 pr-1 rounded-lg text-sm flex items-center gap-2 transition-colors ${
            conversation.id === selectedConversationId
              ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
              : "hover:bg-muted text-muted-foreground border border-transparent"
          }`}
        >
          {isTema ? (
            <GitBranch className="h-3.5 w-3.5 shrink-0" />
          ) : isMain ? (
            <Pin data-testid={`conversation-main-${conversation.id}`} aria-label="Chat principal" className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
          ) : (
            <MessageSquare className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="truncate">{conversation.title}</span>
          {isMain && <span className="shrink-0 rounded-full bg-emerald-500/15 px-1.5 text-[10px] text-emerald-500">Principal</span>}
        </button>
        {!isTema && !isMain && onMakeMain && (
          <button type="button" data-testid={`conversation-make-main-${conversation.id}`} aria-label="Hacer principal" title="Hacer principal" className={ACTION} onClick={() => onMakeMain(conversation)}>
            <Pin className="h-3.5 w-3.5" />
          </button>
        )}
        {onDelete && (
          <button type="button" data-testid={`conversation-delete-${conversation.id}`} aria-label="Eliminar chat" title="Eliminar" className={ACTION} onClick={() => onDelete(conversation)}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// Un chat eliminado: se puede ver (solo lectura), restaurar o eliminar definitivamente. Los temas que se fueron con su chat se restauran con él.
function DeletedRow({ item, depth, parentDeleted, selected, onSelect, onRestore, onPurge }) {
  return (
    <div className="flex items-center gap-0.5" style={{ paddingLeft: `${depth * 16}px` }}>
      <button
        type="button"
        data-testid={`deleted-item-${item.id}`}
        title="Ver (solo lectura)"
        onClick={() => onSelect(item.id)}
        className={`min-w-0 flex-1 text-left py-1.5 pl-3 pr-1 rounded-lg text-xs flex flex-col transition-colors border ${
          selected ? "bg-amber-500/10 text-amber-500 border-amber-500/20" : "hover:bg-muted text-muted-foreground border-transparent"
        }`}
      >
        <span className="truncate">{item.kind === "tema" ? "🧵 " : ""}{item.title}</span>
        <span className="text-[10px] opacity-70">Eliminado el {fmtDate(item.deleted_at)} · {item.message_count} mensajes</span>
      </button>
      <button
        type="button"
        data-testid={`deleted-restore-${item.id}`}
        aria-label="Restaurar"
        title={parentDeleted ? "Se restaura junto con su chat" : "Restaurar"}
        disabled={parentDeleted}
        className={`${ACTION} disabled:opacity-20`}
        onClick={() => onRestore(item)}
      >
        <RotateCcw className="h-3.5 w-3.5" />
      </button>
      {!parentDeleted && (
        <button type="button" data-testid={`deleted-purge-${item.id}`} aria-label="Eliminar definitivamente" title="Eliminar definitivamente" className={ACTION} onClick={() => onPurge(item)}>
          <Trash2 className="h-3.5 w-3.5 text-red-500" />
        </button>
      )}
    </div>
  );
}

function DeletedSection({ items, selectedConversationId, onSelect, onRestore, onPurge }) {
  const [open, setOpen] = useState(false);
  const ids = new Set(items.map((i) => i.id));
  const byParent = {};
  items.forEach((i) => {
    const key = i.parent_conversation_id && ids.has(i.parent_conversation_id) ? i.parent_conversation_id : "root";
    (byParent[key] = byParent[key] || []).push(i);
  });
  return (
    <div data-testid="deleted-section" className="border-t border-border pt-2 mt-2">
      <button type="button" data-testid="deleted-section-toggle" className="w-full text-left px-3 py-1 text-xs text-muted-foreground flex items-center gap-1.5" onClick={() => setOpen((v) => !v)}>
        <span>{open ? "▾" : "▸"}</span> <Trash2 className="h-3 w-3" /> Chats eliminados ({items.length})
      </button>
      {open && (
        <div data-testid="deleted-list" className="space-y-0.5 mt-1">
          {(byParent.root || []).map((item) => (
            <div key={item.id}>
              <DeletedRow item={item} depth={0} parentDeleted={false} selected={item.id === selectedConversationId} onSelect={onSelect} onRestore={onRestore} onPurge={onPurge} />
              {(byParent[item.id] || []).map((child) => (
                <DeletedRow key={child.id} item={child} depth={1} parentDeleted selected={child.id === selectedConversationId} onSelect={onSelect} onRestore={onRestore} onPurge={onPurge} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ConversationList({
  conversations,
  deletedConversations = [],
  selectedConversationId,
  onSelect,
  onNew,
  onMakeMain,
  onDelete,
  onRestore,
  onPurge,
}) {
  const byParent = groupByParent(conversations);
  const roots = [...(byParent["root"] || [])].sort((a, b) => (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0)); // el principal, arriba

  return (
    <div data-testid="conversation-list" className="flex flex-col h-full w-full">
      <div className="p-3">
        <Button data-testid="new-conversation-button" onClick={onNew} className="w-full" size="sm">
          <Plus className="h-4 w-4 mr-1.5" /> Nueva conversación
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 space-y-1 pb-3">
        {flattenTree(roots, byParent).map(({ conversation, depth }) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            depth={depth}
            selectedConversationId={selectedConversationId}
            onSelect={onSelect}
            onMakeMain={onMakeMain}
            onDelete={onDelete}
          />
        ))}
        {roots.length === 0 && (
          <p data-testid="conversation-list-empty" className="text-xs text-muted-foreground px-3 py-2">
            Sin conversaciones aún.
          </p>
        )}
        {deletedConversations.length > 0 && (
          <DeletedSection items={deletedConversations} selectedConversationId={selectedConversationId} onSelect={onSelect} onRestore={onRestore} onPurge={onPurge} />
        )}
      </div>
    </div>
  );
}
